// BEGIN FILE: src/components/TransitDateSlider.tsx
import React, { useCallback, useMemo } from 'react';
import { Box, Button } from '@mui/joy';
import { DateTime } from 'luxon';

interface TransitDateControlProps {
  value: DateTime;
  onChange: (d: DateTime) => void;
}

/**
 * Date navigator for the compact house dashboard.
 * −M / −W  ← [date picker] →  +W / +M   [Today]
 *
 * Drives transitDate in App.tsx → updates both transit planet positions
 * AND the dasha context (MD/AD/PD/SD) for the selected date.
 */
export default function TransitDateSlider({ value, onChange }: TransitDateControlProps) {
  const isToday = useMemo(() => {
    const now = DateTime.now();
    return value.hasSame(now, 'day');
  }, [value]);

  const shift = useCallback(
    (unit: 'days' | 'weeks' | 'months', amount: number) => {
      onChange(value.plus({ [unit]: amount }));
    },
    [value, onChange],
  );

  const handleDateInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = DateTime.fromISO(e.target.value);
      if (parsed.isValid) {
        // Keep current time-of-day
        onChange(parsed.set({ hour: value.hour, minute: value.minute }));
      }
    },
    [value, onChange],
  );

  const handleToday = useCallback(() => {
    onChange(DateTime.now());
  }, [onChange]);

  // native date input value (YYYY-MM-DD)
  const dateInputValue = value.toFormat('yyyy-MM-dd');

  const isFuture = value > DateTime.now();
  const accentColor = isToday ? '#64748b' : isFuture ? '#1d4ed8' : '#92400e';
  const borderColor = isToday ? 'var(--joy-palette-neutral-outlinedBorder)' : isFuture ? '#bfdbfe' : '#fde68a';

  const stepBtn = (label: string, unit: 'days' | 'weeks' | 'months', amount: number) => (
    <Button
      size="sm"
      variant="outlined"
      onClick={() => shift(unit, amount)}
      sx={{
        fontSize: '11px',
        fontWeight: 700,
        minWidth: 'unset',
        px: 0.9,
        py: 0.3,
        minHeight: 'unset',
        lineHeight: 1.4,
        color: amount < 0 ? '#92400e' : '#1d4ed8',
        borderColor: amount < 0 ? '#fde68a' : '#bfdbfe',
        bgcolor: amount < 0 ? '#fffbeb' : '#eff6ff',
        '&:hover': { bgcolor: amount < 0 ? '#fef3c7' : '#dbeafe' },
        flexShrink: 0,
      }}
    >
      {label}
    </Button>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.6,
        mb: 0,
        borderRadius: 'md',
        bgcolor: 'background.level1',
        border: '1px solid',
        borderColor,
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {/* Past step buttons */}
      {stepBtn('−M', 'months', -1)}
      {stepBtn('−W', 'weeks', -1)}
      {stepBtn('−D', 'days', -1)}

      {/* Native date picker */}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
        <input
          type="date"
          value={dateInputValue}
          onChange={handleDateInput}
          style={{
            border: 'none',
            background: 'transparent',
            fontSize: '12px',
            fontWeight: 700,
            color: accentColor,
            cursor: 'pointer',
            outline: 'none',
            width: '100%',
            maxWidth: '130px',
            textAlign: 'center',
          }}
        />
      </Box>

      {/* Future step buttons */}
      {stepBtn('+D', 'days', 1)}
      {stepBtn('+W', 'weeks', 1)}
      {stepBtn('+M', 'months', 1)}

      {/* Today reset */}
      {!isToday && (
        <Button
          size="sm"
          variant="soft"
          color="neutral"
          onClick={handleToday}
          sx={{
            fontSize: '10px',
            py: 0.25,
            px: 0.75,
            minHeight: 'unset',
            lineHeight: 1.4,
            flexShrink: 0,
          }}
        >
          Today
        </Button>
      )}
    </Box>
  );
}
